import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OvertimeTypeSelect } from '@/components/selects';
import { hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';

import { toUtcIsoStringFromCompanyLocal } from '@/utils/displayContext';

const DEFAULT_BREAK_MINUTES = 60;

function pickerDurationMs(checkIn: string, checkOut: string): number {
  const start = toUtcIsoStringFromCompanyLocal(checkIn);
  const end = toUtcIsoStringFromCompanyLocal(checkOut);
  return new Date(end).getTime() - new Date(start).getTime();
}

export function estimateAttendanceOvertimeMinutes(
  checkIn: string,
  checkOut: string,
  expectedMinutes: number,
  breakMinutes = DEFAULT_BREAK_MINUTES,
): number {
  if (!checkIn || !checkOut || checkOut <= checkIn) return 0;
  const worked = Math.max(0, Math.round(pickerDurationMs(checkIn, checkOut) / 60_000));
  const productive = Math.max(0, worked - breakMinutes);
  if (expectedMinutes <= 0) return productive > 0 ? productive : 0;
  return Math.max(0, productive - expectedMinutes);
}

type Props = {
  checkIn: string;
  checkOut: string;
  overtimeType: string;
  onOvertimeTypeChange: (value: string) => void;
  expectedMinutes?: number;
};

export function HrAttendanceOvertimeFields({
  checkIn,
  checkOut,
  overtimeType,
  onOvertimeTypeChange,
  expectedMinutes = 8 * 60,
}: Props) {
  const { t } = useTranslation('hr');
  const estimatedOvertimeMinutes = useMemo(
    () => estimateAttendanceOvertimeMinutes(checkIn, checkOut, expectedMinutes),
    [checkIn, checkOut, expectedMinutes],
  );

  if (!checkIn || !checkOut || checkOut <= checkIn) {
    return null;
  }

  const expectedLabel = hrFormatMinutes(expectedMinutes);
  const description =
    estimatedOvertimeMinutes > 0
      ? t('overtimePage.attendanceFields.estimatedWithType', {
          estimated: hrFormatMinutes(estimatedOvertimeMinutes),
          expected: expectedLabel,
        })
      : t('overtimePage.attendanceFields.expectedBeyondSchedule', { expected: expectedLabel });

  return (
    <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {t('overtimePage.attendanceFields.title')}
      </p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{description}</p>
      <div className="mt-3 max-w-md">
        <OvertimeTypeSelect
          value={overtimeType}
          onChange={onOvertimeTypeChange}
          required={estimatedOvertimeMinutes > 0}
          autoSelectWhenRequired
          placeholder={t('labels.selectOvertimeType')}
        />
      </div>
    </div>
  );
}
